<template>
  <div class="order-details">
    <h1>Order Details</h1>
    <div v-if="isCorrectOrderLoaded">
      <!-- Customer & Order Information (merged) -->
      <div class="info-section">
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">Customer</label>
              <p class="control">
                {{ displayOrder.firstName }} {{ displayOrder.lastName }}
              </p>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">Email</label>
              <p class="control">{{ displayOrder.email }}</p>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">Order ID</label>
              <p class="control">{{ displayOrder.orderId }}</p>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">Order Date</label>
              <p class="control">{{ formatDate(displayOrder.created) }}</p>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">Payment Intent</label>
              <p class="control">
                {{
                  displayOrder.paymentIntent &&
                  displayOrder.paymentIntent.trim()
                    ? displayOrder.paymentIntent
                    : "N/A"
                }}
              </p>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">Discount Code</label>
              <p class="control">
                {{ displayOrder.discountCode || "None" }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Line Items and Order Summary -->
      <div class="section">
        <h4>Order Summary</h4>

        <!-- Line Items Table -->
        <div
          v-if="orderLineItems && orderLineItems.length > 0"
          class="table-wrapper"
        >
          <table class="table is-fullwidth line-items-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Event</th>
                <th>Description</th>
                <th class="is-hidden-mobile">Quantity</th>
                <th class="has-text-right is-hidden-mobile">Price</th>
                <th class="has-text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in orderLineItems" :key="item.id">
                <td>
                  <span class="is-hidden-mobile">{{
                    formatDate(item.date)
                  }}</span>
                  <span class="is-hidden-tablet">{{
                    formatDateShort(item.date)
                  }}</span>
                </td>
                <td>{{ item.eventTitle }}</td>
                <td>
                  <span class="is-hidden-mobile">{{
                    item.description || item.tierName || "Ticket"
                  }}</span>
                  <span class="is-hidden-tablet"
                    >{{ item.quantity }} -
                    {{ item.description || item.tierName || "Ticket" }}</span
                  >
                </td>
                <td class="is-hidden-mobile">{{ item.quantity }}</td>
                <td class="has-text-right is-hidden-mobile">
                  ${{ formatCurrency(item.price) }}
                </td>
                <td class="has-text-right">
                  ${{ formatCurrency(item.total) }}
                </td>
              </tr>
              <!-- Donation line item (only if it exists and > 0) -->
              <tr v-if="displayOrder.donation && displayOrder.donation > 0">
                <td>
                  <span class="is-hidden-mobile">{{
                    formatDate(displayOrder.created)
                  }}</span>
                  <span class="is-hidden-tablet">{{
                    formatDateShort(displayOrder.created)
                  }}</span>
                </td>
                <td>{{ displayOrder.eventTitle }}</td>
                <td>
                  <span class="is-hidden-mobile">Donation</span>
                  <span class="is-hidden-tablet">1 - Donation</span>
                </td>
                <td class="is-hidden-mobile">1</td>
                <td class="has-text-right is-hidden-mobile">
                  ${{ formatCurrency(displayOrder.donation) }}
                </td>
                <td class="has-text-right">
                  ${{ formatCurrency(displayOrder.donation) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Fallback: Show order data if no line items available -->
        <div v-else-if="!loadingLineItems" class="table-wrapper">
          <table class="table is-fullwidth line-items-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Event</th>
                <th>Description</th>
                <th class="is-hidden-mobile">Quantity</th>
                <th class="has-text-right is-hidden-mobile">Price</th>
                <th class="has-text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <span class="is-hidden-mobile">{{
                    formatDate(displayOrder.created)
                  }}</span>
                  <span class="is-hidden-tablet">{{
                    formatDateShort(displayOrder.created)
                  }}</span>
                </td>
                <td>{{ displayOrder.eventTitle }}</td>
                <td>
                  <span class="is-hidden-mobile">{{
                    displayOrder.tierName || "Ticket"
                  }}</span>
                  <span class="is-hidden-tablet"
                    >{{ displayOrder.quantity }} -
                    {{ displayOrder.tierName || "Ticket" }}</span
                  >
                </td>
                <td class="is-hidden-mobile">{{ displayOrder.quantity }}</td>
                <td class="has-text-right is-hidden-mobile">
                  ${{ formatCurrency(displayOrder.price) }}
                </td>
                <td class="has-text-right">
                  ${{ formatCurrency(displayOrder.total) }}
                </td>
              </tr>
              <!-- Donation line item (only if it exists and > 0) -->
              <tr v-if="displayOrder.donation && displayOrder.donation > 0">
                <td>
                  <span class="is-hidden-mobile">{{
                    formatDate(displayOrder.created)
                  }}</span>
                  <span class="is-hidden-tablet">{{
                    formatDateShort(displayOrder.created)
                  }}</span>
                </td>
                <td>{{ displayOrder.eventTitle }}</td>
                <td>
                  <span class="is-hidden-mobile">Donation</span>
                  <span class="is-hidden-tablet">1 - Donation</span>
                </td>
                <td class="is-hidden-mobile">1</td>
                <td class="has-text-right is-hidden-mobile">
                  ${{ formatCurrency(displayOrder.donation) }}
                </td>
                <td class="has-text-right">
                  ${{ formatCurrency(displayOrder.donation) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Loading state for line items -->
        <div v-else-if="loadingLineItems" class="has-text-centered">
          <span class="icon">
            <i class="fas fa-spinner fa-spin"></i>
          </span>
          Loading line items...
        </div>

        <!-- Receipt-style totals on the right -->
        <div class="receipt-totals">
          <div class="total-line" v-if="displayOrder.subtotal">
            <span class="total-label">Subtotal:</span>
            <span class="total-amount"
              >${{ formatCurrency(displayOrder.subtotal) }}</span
            >
          </div>
          <div
            class="total-line"
            v-if="displayOrder.salesTax && displayOrder.salesTax > 0"
          >
            <span class="total-label">Sales Tax:</span>
            <span class="total-amount"
              >${{ formatCurrency(displayOrder.salesTax) }}</span
            >
          </div>
          <div class="total-line total-final">
            <span class="total-label"><strong>Total:</strong></span>
            <span class="total-amount"
              ><strong>${{ formatCurrency(displayOrder.total) }}</strong></span
            >
          </div>
        </div>
      </div>

      <!-- Resend Confirmation Email Button -->
      <div class="resend-email-section">
        <button
          class="button is-primary"
          @click="resendConfirmationEmail"
          :disabled="isResending"
        >
          <span v-if="isResending" class="icon">
            <i class="fas fa-spinner fa-spin"></i>
          </span>
          {{ isResending ? "Sending..." : "Resend Confirmation Email" }}
        </button>

        <!-- Success Message -->
        <div
          v-if="resendSuccess"
          class="notification is-success resend-message"
        >
          <i class="fas fa-check"></i>
          Confirmation email sent successfully!
        </div>

        <!-- Error Message -->
        <div v-if="resendError" class="notification is-danger resend-message">
          <i class="fas fa-exclamation-triangle"></i>
          {{ resendError }}
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-else class="has-text-centered">
      <span class="icon">
        <i class="fas fa-spinner fa-spin"></i>
      </span>
      Loading order details...
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "OrderDetails",
  props: {
    order: {
      type: Object,
      default: null
    },
    eventId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loadingLineItems: false,
      isResending: false,
      resendError: null,
      resendSuccess: false,
      requestListener: null
    };
  },
  computed: {
    ...mapGetters({
      orderLineItems: "orderLineItems",
      fetchedOrder: "order"
    }),
    displayOrder() {
      return this.fetchedOrder && Object.keys(this.fetchedOrder).length > 0
        ? this.fetchedOrder
        : this.order;
    },
    isCorrectOrderLoaded() {
      if (!this.order || !this.order.orderId) return false;
      if (!this.fetchedOrder || Object.keys(this.fetchedOrder).length === 0)
        return false;
      return this.fetchedOrder.orderId === this.order.orderId;
    }
  },
  watch: {
    async order(newOrder, oldOrder) {
      if (
        newOrder &&
        newOrder.orderId &&
        this.eventId &&
        (!oldOrder || oldOrder.orderId !== newOrder.orderId)
      ) {
        this.$store.commit("SET_ORDER", {});
        this.$store.commit("SET_ORDER_LINE_ITEMS", []);

        await this.fetchOrder(newOrder.orderId);
        await this.fetchLineItems(newOrder.orderId);
      }
    }
  },
  async created() {
    if (this.order && this.order.orderId && this.eventId) {
      await this.fetchOrder(this.order.orderId);
      await this.fetchLineItems(this.order.orderId);
    }
  },
  beforeDestroy() {
    if (this.requestListener) {
      this.requestListener();
      this.requestListener = null;
    }
  },
  methods: {
    async fetchOrder(orderId) {
      try {
        await this.$store.dispatch("fetchOrder", {
          eventId: this.eventId,
          orderId: orderId
        });
      } catch (error) {
        console.error("Error fetching order:", error);
      }
    },
    async fetchLineItems(orderId) {
      this.loadingLineItems = true;
      try {
        await this.$store.dispatch("fetchOrderLineItems", {
          eventId: this.eventId,
          orderId: orderId
        });
      } catch (error) {
        console.error("Error fetching line items:", error);
      } finally {
        this.loadingLineItems = false;
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return "N/A";
      if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleDateString();
      }
      if (
        typeof timestamp === "string" &&
        /^\d{4}-\d{2}-\d{2}$/.test(timestamp)
      ) {
        const [year, month, day] = timestamp.split("-");
        return new Date(year, month - 1, day).toLocaleDateString();
      }
      return new Date(timestamp).toLocaleDateString();
    },
    formatDateShort(timestamp) {
      if (!timestamp) return "N/A";
      let date;
      if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (
        typeof timestamp === "string" &&
        /^\d{4}-\d{2}-\d{2}$/.test(timestamp)
      ) {
        const [year, month, day] = timestamp.split("-");
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(timestamp);
      }
      return `${date.getMonth() + 1}/${date.getDate()}`;
    },
    formatCurrency(amount) {
      if (amount == null || amount === undefined) return "0.00";
      return Number(amount).toFixed(2);
    },
    async resendConfirmationEmail() {
      this.isResending = true;
      this.resendError = null;
      this.resendSuccess = false;

      if (this.requestListener) {
        this.requestListener();
        this.requestListener = null;
      }

      try {
        const reqRef = await this.$store.dispatch("resendConfirmationEmail", {
          orderId: this.displayOrder.orderId
        });

        this.requestListener = reqRef.onSnapshot(snapshot => {
          const data = snapshot.data();
          if (data) {
            if (data.status === "complete") {
              this.isResending = false;
              this.resendSuccess = true;
              this.resendError = null;
              if (this.requestListener) {
                this.requestListener();
                this.requestListener = null;
              }
              setTimeout(() => {
                this.resendSuccess = false;
              }, 3000);
            } else if (data.status === "Error") {
              this.isResending = false;
              this.resendError =
                data.errorMessage || "An unknown error occurred";
              this.resendSuccess = false;
              if (this.requestListener) {
                this.requestListener();
                this.requestListener = null;
              }
            }
          }
        });
      } catch (error) {
        console.error("Error resending confirmation email:", error);
        this.isResending = false;
        this.resendError = "Failed to submit resend request";
        this.resendSuccess = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.order-details {
  h1 {
    font-family: $family-primary;
    font-weight: bold;
    margin-bottom: 1rem;
  }
}

.info-section {
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: $gray6;
  border-radius: $radius;
}

.section {
  margin-bottom: 0.75rem;
  padding: 0;
}

h4 {
  font-family: $family-primary;
  font-weight: 600;
  color: $gray2;
  margin-bottom: 0.75rem;
  font-size: 1.1rem;
}

.label {
  color: $gray3;
  font-weight: 600;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
}

.control {
  font-family: $family-primary;
  font-size: 0.9rem;
}

.field {
  margin-bottom: 0.5rem;
}

.table-wrapper {
  overflow-x: auto;
}

.line-items-table {
  margin-bottom: 1rem;
  width: 100%;
}

.receipt-totals {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 0.75rem;
  padding: 0.75rem;
  background-color: $gray6;
  border-radius: $radius;
}

.total-line {
  display: flex;
  justify-content: space-between;
  width: 250px;
  margin-bottom: 0.3rem;
  font-family: $family-primary;
}

.total-final {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid $gray4;
  font-size: 1.1rem;
}

.total-label {
  text-align: left;
}

.total-amount {
  text-align: right;
  min-width: 80px;
}

.resend-email-section {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid $gray4;
  text-align: center;
}

.resend-message {
  margin-top: 0.75rem;
  font-size: 0.9rem;

  i {
    margin-right: 0.5rem;
  }
}

// Mobile styles
@media screen and (max-width: 768px) {
  .info-section {
    padding: 0.5rem;

    .field {
      margin-bottom: 0.25rem;
    }

    .label {
      font-size: 0.75rem;
      margin-bottom: 0.1rem;
    }

    p.control {
      font-size: 0.85rem;
      word-break: break-all;
      margin: 0;
    }
  }

  .line-items-table {
    font-size: 0.8rem;

    th,
    td {
      padding: 0.4em 0.5em;
    }
  }

  .receipt-totals {
    padding: 0.5rem;

    .total-line {
      width: 100%;
    }
  }
}
</style>
